import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { api } from "../../api";
import usePageTitle from "../../hooks/usePageTitle";
import AppShell from "../AppShell";
import ImageWithFallback from "../ImageWithFallback";
import { buildItemUrl } from "../../utils/itemUrl";
import { formatPrice, getPriceDollars } from "../../utils/price";
import { PriceVisibilityBoundary, usePriceVisibility } from "../../contexts/PriceVisibilityContext";
import { useAdaptiveHeroGradient } from "../../utils/adaptiveHeroGradient";
import {
  getOnePieceSetBySlug,
  getOnePieceSetImageCandidates,
  getOnePieceSetTheme,
  sortOnePieceSetItems,
} from "../../utils/onePieceSets";

const panelSx = {
  p: { xs: 2, md: 2.5 },
  borderRadius: 2.5,
  border: "1px solid",
  borderColor: "rgba(12, 35, 64, 0.08)",
  backgroundColor: "#ffffff",
  boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
};

const cardSx = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  p: 1.4,
  borderRadius: 2.25,
  border: "1px solid",
  borderColor: "rgba(12, 35, 64, 0.08)",
  backgroundColor: "#fbfcff",
  textDecoration: "none",
  color: "inherit",
  transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease, background-color 160ms ease",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 16px 30px rgba(15, 23, 42, 0.12)",
    borderColor: "rgba(207, 139, 40, 0.22)",
    backgroundColor: "#ffffff",
  },
};

function PlaceholderImage({ label = "No Image" }) {
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "text.secondary",
        fontWeight: 700,
        letterSpacing: 0.3,
      }}
    >
      {label}
    </Box>
  );
}

function OnePieceSetItemCard({ item, setEntry }) {
  const setImages = getOnePieceSetImageCandidates(setEntry);
  const primaryImage = item?.image_url || item?.ref_image_url || setImages[0] || "";

  return (
    <Box component={RouterLink} to={buildItemUrl(item)} sx={cardSx}>
      <Box
        sx={{
          height: 210,
          borderRadius: 1.75,
          overflow: "hidden",
          backgroundColor: "#eef1f5",
          mb: 1.35,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {primaryImage ? (
          <ImageWithFallback
            src={primaryImage}
            fallbackSrcs={setImages}
            alt={item.name || `${setEntry?.set_name || "One Piece set"} card`}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <PlaceholderImage />
        )}
      </Box>

      <Stack spacing={0.55} sx={{ flexGrow: 1 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 800,
            color: "#10233f",
            lineHeight: 1.3,
            minHeight: 48,
            wordBreak: "break-word",
          }}
        >
          {item?.name || `Item #${item?.item_id}`}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
          {item?.set_name || setEntry?.set_name}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {item?.number ? `No. ${item.number}` : "Number unavailable"}
        </Typography>
        <PriceVisibilityBoundary><Typography variant="body1" sx={{ color: "#b9771d", fontWeight: 800, pt: 0.35 }}>
          {formatPrice(item) || "TBD"}
        </Typography></PriceVisibilityBoundary>
      </Stack>
    </Box>
  );
}

export default function OnePieceSetTemplatePage({ setSlug }) {
  const { showPrices: pricePreferenceEnabled, pricesLoaded } = usePriceVisibility();
  const showPrices = pricesLoaded && pricePreferenceEnabled;
  const initialSetEntry = useMemo(() => (
    getOnePieceSetBySlug(setSlug) || {
      slug: String(setSlug || "").trim().toLowerCase(),
      code: "",
      display_code: "",
      set_name: "One Piece Set",
      notes: "",
    }
  ), [setSlug]);
  const [loadedSetEntry, setLoadedSetEntry] = useState(null);
  const setEntry = loadedSetEntry || initialSetEntry;
  const safeSetName = setEntry?.set_name || "One Piece Set";
  const safeSetCode = setEntry?.display_code || setEntry?.code || "";
  const safeSetSlug = String(setSlug || setEntry?.slug || "").trim().toLowerCase();
  const setTheme = useMemo(
    () => getOnePieceSetTheme(setEntry || {}),
    [setEntry]
  );
  const imageCandidates = useMemo(
    () => getOnePieceSetImageCandidates(setEntry || {}),
    [setEntry]
  );
  const heroVisual = useAdaptiveHeroGradient(
    imageCandidates,
    setTheme?.heroGradient,
    { maxSize: 200 }
  );

  usePageTitle(safeSetName);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortBy, setSortBy] = useState(() => showPrices ? "price_desc" : "item_id_desc");
  useEffect(() => { if (!showPrices && sortBy.startsWith("price_")) setSortBy("item_id_desc"); }, [showPrices, sortBy]);

  useEffect(() => {
    let mounted = true;

    const loadSet = async () => {
      if (!safeSetSlug) {
        if (mounted) {
          setLoadedSetEntry(null);
          setItems([]);
          setError("");
          setLoading(false);
        }
        return;
      }

      try {
        console.log("ONE_PIECE_SET_ROUTE_FETCH", { setSlug: safeSetSlug });
        setLoading(true);
        setError("");
        const [metaResponse, itemsResponse] = await Promise.all([
          api.get(`/api/one-piece/sets/${safeSetSlug}`),
          api.get(`/api/one-piece/sets/${safeSetSlug}/items`),
        ]);
        if (!mounted) return;
        const metaData = metaResponse?.data || {};
        const itemsData = itemsResponse?.data || {};
        const nextItems = Array.isArray(itemsData?.items) ? itemsData.items : [];
        const nextSet = metaData?.set || metaData?.item || itemsData?.set || null;
        setLoadedSetEntry(
          nextSet
          || (nextItems.length
            ? {
                slug: safeSetSlug,
                code: safeSetCode || String(safeSetSlug || "").toUpperCase(),
                display_code: safeSetCode || String(safeSetSlug || "").toUpperCase(),
                set_name: nextItems[0]?.set_name || safeSetName,
                notes: "",
              }
            : initialSetEntry)
        );
        setItems(nextItems);
      } catch (err) {
        if (!mounted) return;
        if (err?.response?.status === 404) {
          setLoadedSetEntry(initialSetEntry);
          setItems([]);
          setError(`No backend route is returning cards for ${safeSetName} yet.`);
          return;
        }
        setLoadedSetEntry(initialSetEntry);
        setItems([]);
        setError(err?.response?.data?.message || err?.message || `Failed to load ${safeSetName} cards.`);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadSet();
    return () => {
      mounted = false;
    };
  }, [initialSetEntry, safeSetCode, safeSetName, safeSetSlug]);

  const pricedCount = useMemo(() => items.filter((item) => getPriceDollars(item) != null).length, [items]);

  const highestPrice = useMemo(() => {
    const values = items.map((item) => getPriceDollars(item)).filter((value) => value != null);
    if (!values.length) return "";
    return formatPrice({ price: Math.max(...values).toFixed(2) });
  }, [items]);

  const displayItems = useMemo(() => sortOnePieceSetItems(items, sortBy), [items, sortBy]);

  if (!safeSetSlug) {
    console.warn("[ONE_PIECE_SET_MISSING_SLUG]", { setSlug });
    return (
      <AppShell>
        <Box sx={{ px: { xs: 0, md: 1 }, py: { xs: 1, md: 1.5 } }}>
          <Paper elevation={0} sx={panelSx}>
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              This One Piece set page is not configured yet.
            </Alert>
          </Paper>
        </Box>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <Box sx={{ px: { xs: 0, md: 1 }, py: { xs: 1, md: 1.5 } }}>
        <Stack spacing={{ xs: 2.5, md: 3 }}>
          <Paper
            elevation={0}
            sx={{
              ...panelSx,
              p: { xs: 2.5, md: 3.25 },
              background: heroVisual.background,
              borderColor: "rgba(255,255,255,0.10)",
              position: "relative",
              overflow: "hidden",
              transition: "background 360ms ease",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(rgba(0,0,0,0.20), rgba(0,0,0,0.20)), radial-gradient(circle at top right, rgba(255,255,255,0.15), transparent 30%), radial-gradient(circle at bottom left, rgba(255,220,150,0.12), transparent 28%)",
                pointerEvents: "none",
              }}
            />
            <Grid container spacing={3} sx={{ position: "relative", zIndex: 1 }} alignItems="center">
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    height: { xs: 240, md: 300 },
                    borderRadius: 2.25,
                    overflow: "hidden",
                    border: "1px solid rgba(255,255,255,0.14)",
                    backgroundColor: "rgba(255,255,255,0.10)",
                    boxShadow: `0 0 44px ${heroVisual.glowColor}`,
                    transition: "box-shadow 360ms ease",
                  }}
                >
                  {imageCandidates.length ? (
                    <ImageWithFallback
                      src={imageCandidates[0]}
                      fallbackSrcs={imageCandidates.slice(1)}
                      alt={safeSetName}
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                  ) : (
                    <PlaceholderImage label={safeSetName} />
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography
                  variant="overline"
                  sx={{
                    display: "block",
                    color: "rgba(255,245,228,0.76)",
                    fontWeight: 800,
                    letterSpacing: 1.15,
                    mb: 1,
                  }}
                >
                  One Piece Set Browse
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    color: "#ffffff",
                    fontWeight: 900,
                    lineHeight: 1.08,
                    mb: 1.1,
                    maxWidth: 760,
                    fontSize: { xs: "2rem", md: "2.7rem" },
                  }}
                >
                  {safeSetName}
                </Typography>
                <Typography
                  sx={{
                    color: "rgba(255,244,226,0.86)",
                    maxWidth: 800,
                    lineHeight: 1.65,
                    fontSize: { xs: "0.98rem", md: "1.02rem" },
                    mb: 1.75,
                  }}
                >
                  Browse {safeSetName} with set details, loaded cards, and pricing in one clean collector view.
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {safeSetCode ? <Chip label={safeSetCode} sx={{ fontWeight: 700, bgcolor: setTheme.chipBg, color: "#fff" }} /> : null}
                  {setEntry.year ? <Chip label={`${setEntry.year}`} sx={{ fontWeight: 700, bgcolor: setTheme.chipBg, color: "#fff" }} /> : null}
                  {setEntry.promo ? <Chip label="Promo Set" sx={{ fontWeight: 700, bgcolor: setTheme.chipBg, color: "#fff" }} /> : null}
                  {setEntry.notes ? <Chip label={setEntry.notes} sx={{ fontWeight: 700, bgcolor: setTheme.chipBg, color: "#fff" }} /> : null}
                  <Chip label={`${items.length} loaded`} sx={{ fontWeight: 700, bgcolor: setTheme.chipBg, color: "#fff" }} />
                  {showPrices && pricedCount ? <Chip label={`${pricedCount} with prices`} sx={{ fontWeight: 700, bgcolor: setTheme.chipBg, color: "#fff" }} /> : null}
                  {showPrices && highestPrice ? <Chip label={`Top price ${highestPrice}`} sx={{ fontWeight: 700, bgcolor: setTheme.chipBg, color: "#fff" }} /> : null}
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          {loading ? (
            <Paper elevation={0} sx={{ ...panelSx, py: { xs: 5, md: 7 } }}>
              <Stack spacing={1.5} alignItems="center" justifyContent="center">
                <CircularProgress size={34} />
                <Typography variant="body1" sx={{ color: "text.secondary" }}>
                  Loading {safeSetName} cards...
                </Typography>
              </Stack>
            </Paper>
          ) : error ? (
            <Paper elevation={0} sx={panelSx}>
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            </Paper>
          ) : (
            <Paper elevation={0} sx={panelSx}>
              <Stack spacing={1.1} sx={{ mb: 2.25 }}>
                <Typography variant="h5" sx={{ fontWeight: 900, color: setTheme.accent }}>
                  {safeSetName} Cards
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 860, lineHeight: 1.65 }}>
                  Browse every {safeSetName} card currently loaded, with direct links to each item page.
                </Typography>
              </Stack>
              <Box
                sx={{
                  display: "flex",
                  alignItems: { xs: "stretch", sm: "center" },
                  justifyContent: "space-between",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1.5,
                  mb: 2.25,
                }}
              >
                <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 700 }}>
                  {displayItems.length} {displayItems.length === 1 ? "Card" : "Cards"}
                </Typography>
                <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 220 } }}>
                  <InputLabel id={`${safeSetSlug || "one-piece-set"}-sort-label`}>Sort Items</InputLabel>
                  <Select
                    labelId={`${safeSetSlug || "one-piece-set"}-sort-label`}
                    value={sortBy}
                    label="Sort Items"
                    onChange={(event) => setSortBy(event.target.value)}
                  >
                    <MenuItem value="item_id_desc">Featured / Newest</MenuItem>
                    <MenuItem value="name_asc">Name A-Z</MenuItem>
                    <MenuItem value="name_desc">Name Z-A</MenuItem>
                    {showPrices && <MenuItem value="price_desc">Price High to Low</MenuItem>}
                    {showPrices && <MenuItem value="price_asc">Price Low to High</MenuItem>}
                    <MenuItem value="item_id_asc">Oldest</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {displayItems.length === 0 ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    borderColor: "rgba(12, 35, 64, 0.08)",
                    backgroundColor: "#f9fbfd",
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#10233f", mb: 0.5 }}>
                    No {safeSetName} cards are available yet
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    No inventory has been returned for this set yet.
                  </Typography>
                </Paper>
              ) : (
                <Grid container spacing={{ xs: 1.5, md: 2 }}>
                  {displayItems.map((entry) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={entry.item_id || `${entry.name}-${entry.number}`}>
                      <OnePieceSetItemCard item={entry} setEntry={setEntry} />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Paper>
          )}
        </Stack>
      </Box>
    </AppShell>
  );
}
